import { Component, inject, OnInit, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { MultiSelectModule } from 'primeng/multiselect';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ApiConstants } from 'src/app/base/api-constants';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { UtilService } from 'src/app/services/util.service';
import { HttpHeaders } from '@angular/common/http';

import { AppGlobalService } from 'src/app/services/app.global.service';

@Component({
  selector: 'app-share-session',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    MultiSelectModule,
    ButtonModule,
    InputTextModule,
    TranslateModule
  ],
  templateUrl: './share-session.component.html',
  styleUrls: ['./share-session.component.scss']
})
export class ShareSessionComponent implements OnInit {
  public ref = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);
  private readonly translate = inject(TranslateService);
  private readonly httpBaseService = inject(HttpBaseService);
  private readonly utilService = inject(UtilService);
  private readonly appGlobalService = inject(AppGlobalService);

  private destroyRef = inject(DestroyRef);

  allUsers: any[] = [];
  userOptions: any[] = [];
  sessionUrl = '';
  activeSession: any = {};
  submitted = false;

  Flags: any = {
    isServiceTriggered: false
  };

  shareUserForm = new FormGroup({
    user: new FormControl<any>(null, [Validators.required]),
    subject: new FormControl<string>('', [Validators.required])
  });

  ngOnInit() {
    this.shareUserForm.controls.subject.setValue(this.translate.instant('INVITE_COLLABORATION_SESSION_SUBJECT'));
    this.sessionUrl = this.config.data?.sessionUrl || '';
    this.activeSession = this.config.data?.activeSession || {};

    this.fetchUsers();
  }

  fetchUsers() {
    const apiConstants = ApiConstants.getUsers;
    const payload = { start: 0, length: 1000 };

    this.httpBaseService[apiConstants.method](apiConstants, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response && response.results) {
            const currentUserEmail = this.appGlobalService.get('userEmail');
            this.allUsers = response.results
              .filter((u: any) => {
                let isCurrentUser = u.email === currentUserEmail;

                if (!isCurrentUser && u.betaAccess === true) {
                  return true;
                }

                return false;
              })
              .map((u: any) => ({
                ...u,
                fullName: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email
              }));
          }
        },
        error: (err: any) => {
          console.error('ShareSession: Error fetching users', err);
        }
      });
  }

  searchForShareUser(event: any) {
    this.userOptions = [];
    const query = event.query?.toLowerCase() || '';
    const filtered: any[] = [];

    for (let i = 0; i < this.allUsers.length; i++) {
      const user = this.allUsers[i];
      const firstName = user?.firstName?.toLowerCase() || '';
      const lastName = user?.lastName?.toLowerCase() || '';
      const email = user?.email?.toLowerCase() || '';

      if (firstName.includes(query) || lastName.includes(query) || email.includes(query)) {
        filtered.push(user);
      }
    }
    this.userOptions = [...filtered];
  }

  sendMail() {
    let payload: any = {};

    payload.subject = this.shareUserForm.value.subject?.trim();
    if (this.shareUserForm.valid && payload.subject) {
      payload.recipientEmails = this.shareUserForm.value.user.map((thatUser: any) => thatUser.email);

      payload.redirectUrl = this.sessionUrl;

      payload.sessionName = this.activeSession.sessionName;
      payload.appName = this.appGlobalService.get('appName');

      this.triggerService(payload);
    }
  }

  close() {
    this.ref.close(false);
  }

  triggerService(payload) {
    const apiConstants = ApiConstants.blueprint.shareSession;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    this.Flags.isServiceTriggered = true;

    return this.httpBaseService[apiConstants.method](apiConstants, payload, {
      headers
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.utilService.showInfo('Link shared successfully');

          this.ref.close(true);

          this.submitted = true;
        },
        error: (serverError: any) => {
          this.Flags.isServiceTriggered = false;
          console.error('Error sharing session', serverError);
        }
      });
  }
}
